import { useCallback, useEffect, useState } from 'react';
import { Modal } from '@cbhq/cds-web/overlays/modal/Modal';
import { ModalHeader } from '@cbhq/cds-web/overlays/modal/ModalHeader';
import { ModalBody } from '@cbhq/cds-web/overlays/modal/ModalBody';
import { VStack } from '@cbhq/cds-web/layout/VStack';
import { HStack } from '@cbhq/cds-web/layout/HStack';
import { TextHeadline } from '@cbhq/cds-web/typography/TextHeadline';
import { TextBody } from '@cbhq/cds-web/typography/TextBody';
import { Link } from '@cbhq/cds-web/typography';
import { Box } from '@cbhq/cds-web/layout/Box';
import { Button } from '@cbhq/cds-web/buttons/Button';
import { useChainId, useAccount } from 'wagmi';
import { Spinner } from '@cbhq/cds-web/loaders/Spinner';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { ethers } from 'ethers';
import { type PermitSingleData, type PermitBatchData } from '@uniswap/permit2-sdk';
import { Radio } from '@cbhq/cds-web/controls/RadioGroup';
import { PERMIT2_ADDRESS } from '@uniswap/permit2-sdk';
import { useERC20Allowances } from '@/onchain/hooks/useERC20Allowance';
import { useERC20Approve } from '@/onchain/hooks/useERC20Approve';
import { getChainById } from '@/onchain/store/supportedChains';
import { type Permit, usePermit2Signature } from '@/onchain/hooks/usePermit2';
import { useWriteContractWithReceipt } from '@/onchain/hooks/useWriteContractWithReceipt';
import { useToken } from '@/onchain/hooks/useToken';

// enum type for execution state
export enum ExecuteState {
  EXECUTE_PENDING_FETCHING_PERMIT2_ALLOWANCE = 0,
  // token0 approval
  EXECUTE_PENDING_PERMIT2_APPROVAL_0 = 1,
  EXECUTE_PENDING_PERMIT2_APPROVAL_0_CONFIRMATION = 2,
  // token1 approval
  EXECUTE_PENDING_PERMIT2_APPROVAL_1 = 3,
  EXECUTE_PENDING_PERMIT2_APPROVAL_1_CONFIRMATION = 4,
  EXECUTE_PENDING_PERMIT2_SIGNATURE = 5,
  EXECUTE_PENDING = 6,
  EXECUTE_CONFIRMATION = 7,
  EXECUTE_SUCCESS = 8,
}

export type ContractExecutionParam = {
  address: string;
  abi: unknown;
  functionName: string;
  args: unknown[];
};

export type PermitAndExecuteModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: (txHash: `0x${string}`) => void;
  onError?: (error: Error) => void;
  permit: Permit;
  contractExecutionDescription?: string;
  getContractExecutionParams: (
    permitData: PermitSingleData | PermitBatchData,
    signature: `0x${string}`,
  ) => ContractExecutionParam;
  dataTestIdPrefix?: string;
  /**
   * When true, renders the content without a Modal wrapper.
   * Useful for embedding the permit/execute flow within another modal.
   * @default false
   */
  inline?: boolean;
};

export function PermitAndExecuteModal(props: PermitAndExecuteModalProps) {
  const {
    open,
    onClose,
    onSuccess,
    onError,
    contractExecutionDescription,
    permit,
    getContractExecutionParams,
    dataTestIdPrefix,
    inline = false,
  } = props;
  const chainId = useChainId();
  const account = useAccount();
  const chain = getChainById(chainId.toString());
  const [executeState, setExecuteState] = useState<ExecuteState>(
    ExecuteState.EXECUTE_PENDING_FETCHING_PERMIT2_ALLOWANCE,
  );
  const [error, setError] = useState<Error | null>(null);

  // support max of two token approvals
  const token0 = permit.details[0]?.token;
  const token0Info = useToken(token0);
  const token1 = permit.details[1]?.token;
  const token1Info = useToken(token1);

  const {
    allowanceList,
    isLoading: isLoadingAllowanceList,
    isError: isErrorAllowanceList,
    error: errorAllowanceList,
    refetch: refetchAllowanceList,
  } = useERC20Allowances(
    permit.details.map((detail) => {
      return {
        tokenAddress: detail.token,
        owner: account.address as `0x${string}`,
        spender: PERMIT2_ADDRESS,
      };
    }),
  );

  const {
    writeError: approve0WriteError,
    isWriteError: isApprove0WriteError,
    isWritePending: isApprove0WritePending,
    confirmError: approve0ConfirmError,
    isConfirmLoading: isApprove0ConfirmLoading,
    isConfirmSuccess: isApprove0ConfirmSuccess,
    isConfirmError: isApprove0ConfirmError,
    write: approve0Write,
    reset: approve0Reset,
  } = useERC20Approve({
    spender: PERMIT2_ADDRESS,
    amount: ethers.constants.MaxUint256.toBigInt(),
    tokenAddress: token0,
  });

  const {
    writeError: approve1WriteError,
    isWriteError: isApprove1WriteError,
    isWritePending: isApprove1WritePending,
    confirmError: approve1ConfirmError,
    isConfirmLoading: isApprove1ConfirmLoading,
    isConfirmSuccess: isApprove1ConfirmSuccess,
    isConfirmError: isApprove1ConfirmError,
    write: approve1Write,
    reset: approve1Reset,
  } = useERC20Approve({
    spender: PERMIT2_ADDRESS,
    amount: ethers.constants.MaxUint256.toBigInt(),
    tokenAddress: token1,
  });

  const {
    signPermit,
    permitData,
    signature,
    isConfirmLoading: isPermitConfirmLoading,
    error: permitError,
    reset: permitReset,
  } = usePermit2Signature();

  const {
    hash: executionHash,
    writeError: executionWriteError,
    isWriteError: isExecutionWriteError,
    isWritePending: isExecutionWritePending,
    confirmError: executionConfirmError,
    isConfirmLoading: isExecutionConfirmLoading,
    isConfirmSuccess: isExecutionConfirmSuccess,
    isConfirmError: isExecutionConfirmError,
    writeContractAsync: executionWriteContractAsync,
    reset: executionResetState,
  } = useWriteContractWithReceipt();

  const resetState = useCallback(async () => {
    approve0Reset();
    approve1Reset();
    executionResetState();
    permitReset();
    void refetchAllowanceList();
    setExecuteState(ExecuteState.EXECUTE_PENDING_FETCHING_PERMIT2_ALLOWANCE);
    setError(null);
  }, [approve0Reset, approve1Reset, executionResetState, permitReset, refetchAllowanceList]);

  // Determine if the state machine should run
  // In inline mode, always run when mounted; in modal mode, only run when open
  const shouldRun = inline ? true : open;

  // state transitions
  useEffect(() => {
    const helper = async () => {
      if (!permit || !getContractExecutionParams || !shouldRun || !account) return;
      switch (executeState) {
        case ExecuteState.EXECUTE_PENDING_FETCHING_PERMIT2_ALLOWANCE:
          if (!isLoadingAllowanceList && !!allowanceList) {
            // Check if all permits have sufficient allowance
            const hasSufficientAllowances = permit.details.every((detail, index) => {
              if (!detail) return true; // Skip if no permit detail
              const allowance = allowanceList[index]?.result as bigint;
              return allowance >= detail.amount;
            });

            setExecuteState(
              hasSufficientAllowances
                ? ExecuteState.EXECUTE_PENDING_PERMIT2_SIGNATURE
                : ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0,
            );
          } else if (isErrorAllowanceList) {
            setError(errorAllowanceList);
          }
          break;
        case ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0:
          if (!allowanceList) break;
          // skip approval if the allowance is enough
          if (!!allowanceList && (allowanceList[0]?.result as bigint) >= permit.details[0].amount) {
            setExecuteState(ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1);
            break;
          }

          if (!isApprove0WritePending && !isApprove0ConfirmLoading && !isApprove0WriteError) {
            void approve0Write();
          }
          if (isApprove0ConfirmLoading) {
            setExecuteState(ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0_CONFIRMATION);
          }
          // jump to error state if any error occurs, such as user rejecting the transaction
          if (isApprove0WriteError) {
            setError(approve0WriteError);
          }
          break;
        case ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0_CONFIRMATION:
          if (isApprove0ConfirmSuccess) {
            setExecuteState(ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1);
          }
          // jump to error state if any error occurs
          if (isApprove0ConfirmError) {
            setError(approve0ConfirmError);
          }
          break;
        case ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1:
          if (!allowanceList) break;
          if (allowanceList.length <= 1) {
            // skip the second approval if we only need one approval
            setExecuteState(ExecuteState.EXECUTE_PENDING_PERMIT2_SIGNATURE);
            break;
          }
          // skip approval if the allowance is enough
          if (!!allowanceList && (allowanceList[1]?.result as bigint) >= permit.details[1].amount) {
            setExecuteState(ExecuteState.EXECUTE_PENDING_PERMIT2_SIGNATURE);
            break;
          }

          if (!isApprove1WritePending && !isApprove1ConfirmLoading && !isApprove1WriteError) {
            void approve1Write();
          }
          if (isApprove1ConfirmLoading) {
            setExecuteState(ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1_CONFIRMATION);
          }
          // jump to error state if any error occurs, such as user rejecting the transaction
          if (isApprove1WriteError) {
            setError(approve1WriteError);
          }
          break;
        case ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1_CONFIRMATION:
          if (isApprove1ConfirmSuccess) {
            setExecuteState(ExecuteState.EXECUTE_PENDING_PERMIT2_SIGNATURE);
          }
          // jump to error state if any error occurs
          if (isApprove1ConfirmError) {
            setError(approve1ConfirmError);
          }
          break;
        case ExecuteState.EXECUTE_PENDING_PERMIT2_SIGNATURE:
          if (!isPermitConfirmLoading && !signature && !permitError) {
            // add liquidity requires batch permit even if there is only one approval
            // so we need to force batch permit here
            void signPermit({ permit, forceBatchPermit: true });
          }
          if (signature) {
            setExecuteState(ExecuteState.EXECUTE_PENDING);
          }
          // jump to error state if any error occurs
          if (permitError) {
            setError(permitError);
          }
          break;
        case ExecuteState.EXECUTE_PENDING:
          if (!isExecutionWritePending && !isExecutionConfirmLoading && !isExecutionWriteError) {
            if (permitData && signature) {
              void executionWriteContractAsync(
                getContractExecutionParams(permitData, signature as `0x${string}`),
              );
            }
          }
          if (isExecutionConfirmLoading) {
            setExecuteState(ExecuteState.EXECUTE_CONFIRMATION);
          }
          // jump to error state if any error occurs, such as user rejecting the transaction
          if (isExecutionWriteError) {
            setError(executionWriteError);
          }
          break;
        case ExecuteState.EXECUTE_CONFIRMATION:
          if (isExecutionConfirmSuccess) {
            setExecuteState(ExecuteState.EXECUTE_SUCCESS);
            onSuccess?.(executionHash as `0x${string}`);
          }
          // jump to error state if any error occurs
          if (isExecutionConfirmError) {
            setError(executionConfirmError);
            onError?.(executionConfirmError ?? new Error('Unknown error during execution'));
          }
          break;
        default:
          break;
      }
    };
    void helper();
  }, [
    account,
    allowanceList,
    approve0ConfirmError,
    approve0Write,
    approve0WriteError,
    approve1ConfirmError,
    approve1Write,
    approve1WriteError,
    errorAllowanceList,
    executeState,
    executionConfirmError,
    executionHash,
    executionWriteContractAsync,
    executionWriteError,
    getContractExecutionParams,
    isApprove0ConfirmError,
    isApprove0ConfirmLoading,
    isApprove0ConfirmSuccess,
    isApprove0WriteError,
    isApprove0WritePending,
    isApprove1ConfirmError,
    isApprove1ConfirmLoading,
    isApprove1ConfirmSuccess,
    isApprove1WriteError,
    isApprove1WritePending,
    isErrorAllowanceList,
    isExecutionConfirmError,
    isExecutionConfirmLoading,
    isExecutionConfirmSuccess,
    isExecutionWriteError,
    isExecutionWritePending,
    isLoadingAllowanceList,
    isPermitConfirmLoading,
    onError,
    onSuccess,
    shouldRun,
    permit,
    permitData,
    permitError,
    signPermit,
    signature,
  ]);

  const handleModalClose = useCallback(() => {
    void resetState();
    onClose();
  }, [onClose, resetState]);

  // Extract content for reuse in both modal and inline modes
  const content = (
    <VStack padding={2} alignItems="center" width="100%" gap={1}>
      {/* permit2 actions */}
      <VStack padding={2} alignItems="flex-start" width="100%" gap={1}>
        <HStack gap={1} alignItems="center" width="100%">
          <Radio checked readOnly />
          <TextHeadline as="p" color="fgMuted">
            Sign the following approvals & transactions
          </TextHeadline>
        </HStack>
        <VStack alignItems="flex-start" width="100%" gap={1} paddingStart={5}>
          <HStack padding={0}>
            <VStack padding={0} alignItems="flex-start" gap={0}>
              <HStack gap={1} alignItems="center" width="100%">
                {executeState < ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0 && (
                  <Radio checked={false} readOnly />
                )}
                {(executeState == ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0 ||
                  executeState == ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0_CONFIRMATION) && (
                  <Spinner size={2} />
                )}
                {executeState > ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0_CONFIRMATION && (
                  <Icon name="circleCheckmark" size="m" active />
                )}
                <TextHeadline as="p" color="fgMuted">
                  Approve Permit2 allowance for {token0Info.symbol ?? ''}
                </TextHeadline>
              </HStack>
              {executeState == ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_0_CONFIRMATION && (
                <TextBody as="p" color="fgMuted" paddingStart={4}>
                  Wait for transaction confirmations...
                </TextBody>
              )}
            </VStack>
          </HStack>
          {token1 && (
            <HStack padding={0}>
              <VStack padding={0} alignItems="flex-start" gap={0}>
                <HStack gap={1} alignItems="center" width="100%">
                  {executeState < ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1 && (
                    <Radio checked={false} readOnly />
                  )}
                  {(executeState == ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1 ||
                    executeState ==
                      ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1_CONFIRMATION) && (
                    <Spinner size={2} />
                  )}
                  {executeState > ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1_CONFIRMATION && (
                    <Icon name="circleCheckmark" size="m" active />
                  )}
                  <TextHeadline as="p" color="fgMuted">
                    Approve Permit2 allowance for {token1Info.symbol ?? ''}
                  </TextHeadline>
                </HStack>
                {executeState == ExecuteState.EXECUTE_PENDING_PERMIT2_APPROVAL_1_CONFIRMATION && (
                  <TextBody as="p" color="fgMuted" paddingStart={4}>
                    Wait for transaction confirmations...
                  </TextBody>
                )}
              </VStack>
            </HStack>
          )}
          <HStack gap={1} alignItems="center" width="100%">
            {executeState < ExecuteState.EXECUTE_PENDING_PERMIT2_SIGNATURE && (
              <Radio checked={false} readOnly />
            )}
            {executeState == ExecuteState.EXECUTE_PENDING_PERMIT2_SIGNATURE && <Spinner size={2} />}
            {executeState > ExecuteState.EXECUTE_PENDING_PERMIT2_SIGNATURE && (
              <Icon name="circleCheckmark" size="m" active />
            )}
            <TextHeadline as="p" color="fgMuted">
              {`Approve the contract to spend your ${token0Info?.symbol ?? 'token'}${
                token1 ? ` and ${token1Info.symbol}` : ''
              } (via Permit2)`}
            </TextHeadline>
          </HStack>
          <TextBody as="p" color="fgMuted">
            <Link href="https://docs.verifiedpools.com/permit2" openInNewWindow underline={false}>
              What is Permit2?
            </Link>
          </TextBody>
        </VStack>
      </VStack>
      {/* contract execution */}
      <VStack padding={2} alignItems="flex-start" width="100%" gap={1}>
        <HStack gap={1} alignItems="center" width="100%">
          <Radio checked={executeState >= ExecuteState.EXECUTE_PENDING} readOnly />
          <TextHeadline as="p" color="fgMuted">
            Confirm the contract execution
          </TextHeadline>
        </HStack>
        <VStack alignItems="flex-start" width="100%" gap={1} paddingStart={5}>
          <HStack padding={0}>
            <VStack padding={0} alignItems="flex-start" gap={0}>
              <HStack gap={1} alignItems="center" width="100%">
                {executeState < ExecuteState.EXECUTE_PENDING && <Radio checked={false} readOnly />}
                {(executeState == ExecuteState.EXECUTE_PENDING ||
                  executeState == ExecuteState.EXECUTE_CONFIRMATION) && <Spinner size={2} />}
                {executeState == ExecuteState.EXECUTE_SUCCESS && (
                  <Icon name="circleCheckmark" size="m" active />
                )}
                <TextHeadline as="p" color="fgMuted">
                  {contractExecutionDescription ?? 'Confirm the transaction in your wallet'}
                </TextHeadline>
              </HStack>
              {executeState == ExecuteState.EXECUTE_CONFIRMATION && (
                <TextBody as="p" color="fgMuted" paddingStart={4}>
                  Wait for transaction confirmations...
                </TextBody>
              )}
            </VStack>
          </HStack>
        </VStack>
      </VStack>
      {/* block explorer */}
      {executeState === ExecuteState.EXECUTE_SUCCESS && (
        <Button
          href={`${chain?.blockExplorers?.default.url}/tx/${executionHash}`}
          target="_blank"
          rel="noreferrer noopener"
          variant="secondary"
          testID={`${dataTestIdPrefix}-explorer-button`}
          as="a"
        >
          View on Explorer
        </Button>
      )}
      {/* error */}
      {error && (
        <Box
          width="100%"
          flexWrap="wrap"
          overflow="scroll"
          background="bgSecondary"
          padding={1}
          testID={`${dataTestIdPrefix}-error-box`}
        >
          <TextBody as="p" overflow="wrap" testID={`${dataTestIdPrefix}-error-message`}>
            {typeof error?.message === 'object'
              ? 'Error during transaction processing. Please try again.'
              : error?.message ?? ''}
          </TextBody>
        </Box>
      )}
    </VStack>
  );

  // Inline mode: return content only (for embedding in another modal)
  if (inline) {
    return content;
  }

  // Modal mode: wrap in Modal (existing behavior)
  return (
    <Modal
      visible={open}
      onRequestClose={handleModalClose}
      shouldCloseOnEscPress={false}
      testID={`${dataTestIdPrefix}-modal`}
    >
      <ModalHeader title="Confirm Transaction" testID={`${dataTestIdPrefix}-modal-header`} />
      <ModalBody testID={`${dataTestIdPrefix}-modal-body`}>{content}</ModalBody>
    </Modal>
  );
}
